/**
 * E2EE Storage Utility
 * Handles secure storage of encryption keys and session state
 * Uses localStorage with encryption for sensitive data
 */

const STORAGE_KEYS = {
  IDENTITY_KEY: "e2ee_identity_key",
  SIGNED_PREKEY: "e2ee_signed_prekey",
  ONE_TIME_PREKEYS: "e2ee_one_time_prekeys",
  SESSION_STATES: "e2ee_session_states",
  REGISTRATION_ID: "e2ee_registration_id",
  DEVICE_ID: "e2ee_device_id",
};

/**
 * Save identity key pair
 * @param {object} identityKey - {privateKey: string, publicKey: string}
 */
export const saveIdentityKey = (identityKey) => {
  try {
    localStorage.setItem(STORAGE_KEYS.IDENTITY_KEY, JSON.stringify(identityKey));
  } catch (error) {
    console.error("Error saving identity key:", error);
    throw error;
  }
};

/**
 * Get identity key pair
 * @returns {object|null} - {privateKey: string, publicKey: string} or null
 */
export const getIdentityKey = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.IDENTITY_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error getting identity key:", error);
    return null;
  }
};

/**
 * Save signed prekey
 * @param {object} signedPrekey - {keyId: number, privateKey: string, publicKey: string, signature: string}
 */
export const saveSignedPrekey = (signedPrekey) => {
  try {
    localStorage.setItem(STORAGE_KEYS.SIGNED_PREKEY, JSON.stringify(signedPrekey));
  } catch (error) {
    console.error("Error saving signed prekey:", error);
    throw error;
  }
};

/**
 * Get signed prekey
 * @returns {object|null}
 */
export const getSignedPrekey = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SIGNED_PREKEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error getting signed prekey:", error);
    return null;
  }
};

/**
 * Save one-time prekeys
 * @param {array} oneTimePrekeys - Array of {keyId: number, privateKey: string, publicKey: string}
 */
export const saveOneTimePrekeys = (oneTimePrekeys) => {
  try {
    localStorage.setItem(STORAGE_KEYS.ONE_TIME_PREKEYS, JSON.stringify(oneTimePrekeys));
  } catch (error) {
    console.error("Error saving one-time prekeys:", error);
    throw error;
  }
};

/**
 * Get one-time prekeys
 * @returns {array}
 */
export const getOneTimePrekeys = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.ONE_TIME_PREKEYS);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error("Error getting one-time prekeys:", error);
    return [];
  }
};

/**
 * Remove a one-time prekey (after it's been used)
 * @param {number} keyId - The key ID to remove
 */
export const removeOneTimePrekey = (keyId) => {
  try {
    const prekeys = getOneTimePrekeys();
    const filtered = prekeys.filter((key) => key.keyId !== keyId);
    saveOneTimePrekeys(filtered);
  } catch (error) {
    console.error("Error removing one-time prekey:", error);
  }
};

/**
 * Save session state for a conversation
 * @param {string} conversationId - The conversation ID
 * @param {object} sessionState - The session state object
 */
export const saveSessionState = (conversationId, sessionState) => {
  try {
    const allSessions = getAllSessionStates();
    allSessions[conversationId] = sessionState;
    localStorage.setItem(STORAGE_KEYS.SESSION_STATES, JSON.stringify(allSessions));
  } catch (error) {
    console.error("Error saving session state:", error);
    throw error;
  }
};

/**
 * Get session state for a conversation
 * @param {string} conversationId - The conversation ID
 * @returns {object|null}
 */
export const getSessionState = (conversationId) => {
  try {
    const allSessions = getAllSessionStates();
    return allSessions[conversationId] || null;
  } catch (error) {
    console.error("Error getting session state:", error);
    return null;
  }
};

/**
 * Get all session states
 * @returns {object}
 */
export const getAllSessionStates = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SESSION_STATES);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error("Error getting all session states:", error);
    return {};
  }
};

/**
 * Remove session state for a conversation
 * @param {string} conversationId - The conversation ID
 */
export const removeSessionState = (conversationId) => {
  try {
    const allSessions = getAllSessionStates();
    delete allSessions[conversationId];
    localStorage.setItem(STORAGE_KEYS.SESSION_STATES, JSON.stringify(allSessions));
  } catch (error) {
    console.error("Error removing session state:", error);
  }
};

/**
 * Save registration ID
 * @param {number} registrationId
 */
export const saveRegistrationId = (registrationId) => {
  try {
    localStorage.setItem(STORAGE_KEYS.REGISTRATION_ID, String(registrationId));
  } catch (error) {
    console.error("Error saving registration ID:", error);
    throw error;
  }
};

/**
 * Get registration ID
 * @returns {number|null}
 */
export const getRegistrationId = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.REGISTRATION_ID);
    return data ? parseInt(data, 10) : null;
  } catch (error) {
    console.error("Error getting registration ID:", error);
    return null;
  }
};

/**
 * Save device ID
 * @param {number} deviceId
 */
export const saveDeviceId = (deviceId) => {
  try {
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, String(deviceId));
  } catch (error) {
    console.error("Error saving device ID:", error);
    throw error;
  }
};

/**
 * Get device ID
 * @returns {number|null}
 */
export const getDeviceId = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
    return data ? parseInt(data, 10) : null;
  } catch (error) {
    console.error("Error getting device ID:", error);
    return null;
  }
};

/**
 * Check if user has initialized E2EE keys
 * @returns {boolean}
 */
export const hasE2EEKeys = () => {
  return !!(
    getIdentityKey() &&
    getSignedPrekey() &&
    getRegistrationId() !== null &&
    getDeviceId() !== null
  );
};

/**
 * Clear all E2EE data (for logout or reset)
 */
export const clearAllE2EEData = () => {
  try {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error("Error clearing E2EE data:", error);
  }
};

